import moment from 'moment';

import ProgressIcon from '../assets/images/progress-new.svg';
import AwaitingReviewIcon from '../assets/images/awaiting-review-new.svg';
import AwaitingFinalisationIcon from '../assets/images/awaiting.svg';
import EditIcon from '../assets/images/edit-new.svg';
import CheckIcon from '../assets/images/check-new.svg';
import AwaitingGuardianSignIcon from '../assets/images/awaiting-guardian-sign.svg';
import { FORTA_BLUE, FORTA_ORANGE, FORTA_PINK, FORTA_SUCCESS } from './colors';
import {
  AssignmentStatus,
  Themes,
  SupervisionNoteStatus,
  GenericSearchType,
  AssessmentNoteStatus,
  Roles,
  BTSessionStatus,
  InvoiceStatus,
  QUESTIONNAIRE_STATUS,
} from '../constants';
import { CQAReviewStatus } from '../components/layout/qa/constants';
import {
  QUESTIONNAIRE_HISTORY_STATUS_LABELS,
  QUESTIONNAIRE_HISTORY_TYPE,
} from '../components/layout/billing-history/constants';
import { ClaimStatus } from '../components/layout/billings/claim-info/constants';
import { areUserRolesIn } from '../helpers';

import type { ISearchConfig } from '../components/base/FHGenericSearch';
import type {
  IClientAssessment,
  IProviderAssignment,
  ISessionMinified,
  IBTSession,
  Bays,
} from './types';
import type { Moment } from 'moment';

export const getBayValueWithoutPrefix = (bay: Bays | undefined) => {
  return bay?.replace('BAY ', '') ?? '';
};

export const getSessionStatus = (session: IBTSession | ISessionMinified) => {
  if (
    session?.status === BTSessionStatus.AwaitingReview &&
    session?.clinical_qa_review?.status === CQAReviewStatus.Acknowledged
  ) {
    return {
      color: FORTA_BLUE.primary,
      icon: AwaitingFinalisationIcon,
      borderColor: FORTA_PINK,
      iconWidth: 32,
      iconHeight: 32,
      status: 'Awaiting finalization',
      statusId: BTSessionStatus.AwaitingReview,
    };
  }

  switch (session?.status) {
    case BTSessionStatus.Started:
      return {
        color: FORTA_BLUE.primary,
        icon: ProgressIcon,
        borderColor: FORTA_BLUE.bright,
        iconWidth: 32,
        iconHeight: 32,
        status: 'In progress',
        statusId: BTSessionStatus.Started,
      };
    case BTSessionStatus.AwaitingReview:
      return {
        color: FORTA_BLUE.primary,
        icon: AwaitingReviewIcon,
        borderColor: FORTA_BLUE.primary,
        iconWidth: 32,
        iconHeight: 32,
        status: 'Awaiting review',
        statusId: BTSessionStatus.AwaitingReview,
      };
    case BTSessionStatus.EditsRequested:
      return {
        color: FORTA_ORANGE,
        icon: EditIcon,
        borderColor: FORTA_ORANGE,
        iconWidth: 32,
        iconHeight: 32,
        status: 'Edits requested',
        statusId: BTSessionStatus.EditsRequested,
      };
    case BTSessionStatus.Completed:
      return {
        color: FORTA_SUCCESS,
        icon: CheckIcon,
        borderColor: FORTA_SUCCESS,
        iconWidth: 32,
        iconHeight: 32,
        status: 'Completed',
        statusId: BTSessionStatus.Completed,
      };
    case BTSessionStatus.AwaitingGuardianSignature:
      return {
        color: FORTA_BLUE.bright,
        icon: AwaitingGuardianSignIcon,
        borderColor: FORTA_BLUE.bright,
        iconWidth: 32,
        iconHeight: 32,
        status: 'Awaiting guardian signature',
        statusId: BTSessionStatus.AwaitingGuardianSignature,
      };
    default:
      break;
  }
};

export const getSupervisionStatus = (
  status: SupervisionNoteStatus | AssessmentNoteStatus | undefined,
) => {
  switch (status) {
    case SupervisionNoteStatus.Started:
      return {
        color: FORTA_BLUE.primary,
        icon: ProgressIcon,
        iconWidth: 32,
        iconHeight: 32,
        status: 'In progress',
        borderColor: FORTA_BLUE.bright,
      };
    case SupervisionNoteStatus.Editing:
      return {
        color: FORTA_BLUE.primary,
        icon: ProgressIcon,
        iconWidth: 32,
        iconHeight: 32,
        status: 'In progress',
        borderColor: FORTA_BLUE.bright,
      };
    case SupervisionNoteStatus.Completed:
      return {
        color: FORTA_SUCCESS,
        icon: CheckIcon,
        iconWidth: 32,
        iconHeight: 32,
        status: 'Success',
        borderColor: FORTA_SUCCESS,
      };
    default:
      break;
  }
};

export const formatPhoneNum = (value: string) => {
  return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
};

export const getUtcOffset = (date: Moment) => {
  return date.utcOffset() * 60;
};

export const getRangeArray = (min: number, max: number) => {
  return Array.from({ length: max - min + 1 }, (_, i) => i + min);
};

export const getSessionSearchStr = (key: string, value: any) => {
  switch (key) {
    case 'startDate':
      return `start=${value.toISOString()}`;
    case 'endDate':
      return `end=${value.toISOString()}`;
    case 'action_required':
      return `action_required=${value}`;
    case 'status':
      return `status=${value}`;
    case 'client_id':
      return `client_id=${value}`;
    case 'include_qa_stats':
      return `include_qa_stats=${value}`;
    case 'bay':
      return `bay=${value}`;
    case 'expand':
      return `expand=${value}`;
    case 'include_participated_only':
      return `include_participated_only=${value}`;
    case 'pending_parent_signature':
      return `pending_parent_signature=${value}`;
    case 'find_by_provider_id_only':
      return `find_by_provider_id_only=${value}`;
    default:
      break;
  }
};

export const getSupervisionSearchStr = (key: string, value: any) => {
  switch (key) {
    case 'startDate':
      return `start=${value.toISOString()}`;
    case 'endDate':
      return `end=${value.toISOString()}`;
    case 'client_id':
      return `client_id=${value}`;
    case 'status':
      return `status=${value}`;
    case 'status_id':
      return `status_id=${value}`;
    case 'service_code':
      return `service_code=${value}`;
    case 'assessment_type':
      return `assessment_type=${value}`;
    case 'action_required':
      return `action_required=${value}`;
    default:
      break;
  }
};

export enum HistorySearchParams {
  StartDate = 'date_start',
  EndDate = 'date_end',
  ClaimId = 'claim_id',
  BillerId = 'biller_id',
  ClientId = 'client_id',
  ClientSupervisionId = 'client_supervision_id',
  ParentSupervisionId = 'parent_supervision_id',
  GroupParentSupervisionId = 'group_parent_supervision_id',
  SessionId = 'session_id',
  ClientAssessmentId = 'client_assessment_id',
  PayorClaimId = 'payor_claim_id',
  Status = 'status',
  SecondaryStatus = 'secondary_status',
  StatusId = 'status_id',
  ClearingHouseClaimId = 'clearing_house_claim_id',
  ProviderId = 'provider_id',
  ProviderName = 'provider_name',
  ClientIdSearch = 'client_id_search',
  QuestionnaireType = 'questionnaire_type',
}

// Provider Name / Client ID are alternate inputs for the same API params (provider_id / client_id),
// so their config ids are aliased to the real params before hitting the questionnaire endpoints.
type QuestionnaireSearchParamAliases = Record<string, HistorySearchParams>;

function defineQuestionnaireSearchParamAliases<T extends QuestionnaireSearchParamAliases>(
  aliases: T,
): T & QuestionnaireSearchParamAliases {
  return aliases;
}

export const QUESTIONNAIRE_SEARCH_PARAM_ALIASES = defineQuestionnaireSearchParamAliases({
  [HistorySearchParams.ProviderName]: HistorySearchParams.ProviderId,
  [HistorySearchParams.ClientIdSearch]: HistorySearchParams.ClientId,
});

export const questionnaireSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.QuestionnaireType,
    label: 'Questionnaire type',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: QUESTIONNAIRE_HISTORY_TYPE.ONBOARDING, label: QUESTIONNAIRE_HISTORY_TYPE.ONBOARDING },
      { id: QUESTIONNAIRE_HISTORY_TYPE.INTAKE, label: QUESTIONNAIRE_HISTORY_TYPE.INTAKE },
    ],
  },
  {
    id: HistorySearchParams.Status,
    label: 'Questionnaire status',
    type: GenericSearchType.AutoComplete,
    options: Object.values(QUESTIONNAIRE_STATUS).map((item) => ({
      id: item,
      label: QUESTIONNAIRE_HISTORY_STATUS_LABELS[item],
    })),
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client name',
    type: GenericSearchType.AutoComplete,
    filterGroupName: 'questionnaireClientTarget',
    options: [],
  },
  {
    id: HistorySearchParams.ClientIdSearch,
    label: 'Client ID',
    type: GenericSearchType.TextInput,
    filterGroupName: 'questionnaireClientTarget',
    options: [],
  },
  {
    id: HistorySearchParams.ProviderId,
    label: 'Guardian ID',
    type: GenericSearchType.TextInput,
    filterGroupName: 'questionnaireProviderTarget',
    options: [],
  },
  {
    id: HistorySearchParams.ProviderName,
    label: 'Guardian name',
    type: GenericSearchType.AutoComplete,
    filterGroupName: 'questionnaireProviderTarget',
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date Range',
    defaultValue: [moment().subtract(14, 'days'), moment()],
    disableDelete: true,
    type: GenericSearchType.DateRange,
  },
];

export const claimSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.ClientAssessmentId,
    label: 'Assessment ID (97151/97152)',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter FA000001',
  },
  {
    id: HistorySearchParams.SessionId,
    label: 'Session ID (97153)',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter F000001',
  },
  {
    id: HistorySearchParams.ClientSupervisionId,
    label: 'Client supervision ID (97155)',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter FCSN000001',
  },
  {
    id: HistorySearchParams.ParentSupervisionId,
    label: 'Parent supervision ID (97156)',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter FPSN000001',
  },
  {
    id: HistorySearchParams.GroupParentSupervisionId,
    label: 'Group parent training (97157)',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter FGP000001',
  },
  {
    id: HistorySearchParams.BillerId,
    label: 'Biller name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: HistorySearchParams.ClaimId,
    label: 'Claim ID',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter FCL000001',
  },
  {
    id: HistorySearchParams.Status,
    label: 'Claim status',
    type: GenericSearchType.AutoComplete,
    options: Object.values(ClaimStatus).map((item) => ({
      id: item,
      label: item,
    })),
  },
  {
    id: 'dateRange',
    label: 'Claim date',
    type: GenericSearchType.DateRange,
  },
  {
    id: HistorySearchParams.SecondaryStatus,
    label: 'Secondary claim status',
    type: GenericSearchType.AutoComplete,
    options: Object.values(ClaimStatus).map((item) => ({
      id: item,
      label: item,
    })),
  },
  {
    id: HistorySearchParams.ClearingHouseClaimId,
    label: 'Clearinghouse ID',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter',
  },
  {
    id: HistorySearchParams.PayorClaimId,
    label: 'Payor claim ID',
    type: GenericSearchType.TextInput,
    filterGroupName: 'claimSearchTargetId',
    placeholder: 'Enter',
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
];

export const assessmentSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.Status,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 'STARTED', label: 'Started' },
      { id: 'COMPLETED', label: 'Completed' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    defaultValue: [moment().subtract(14, 'days'), moment()],
    disableDelete: true,
    type: GenericSearchType.DateRange,
  },
  {
    id: 'service_code',
    label: 'Assessment Type',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 97151, label: '97151' },
      { id: 97152, label: '97152' },
    ],
  },
];

export const clientSNSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.StatusId,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 1, label: 'Started' },
      { id: 2, label: 'Completed' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    disableDelete: true,
    defaultValue: [moment().subtract(14, 'days'), moment()],
    type: GenericSearchType.DateRange,
  },
];

export const btSNSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.StatusId,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 1, label: 'Started' },
      { id: 2, label: 'Completed' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    disableDelete: true,
    defaultValue: [moment().subtract(14, 'days'), moment()],
    type: GenericSearchType.DateRange,
  },
];

export const parentSNSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.StatusId,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 1, label: 'Started' },
      { id: 2, label: 'Completed' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    disableDelete: true,
    defaultValue: [moment().subtract(14, 'days'), moment()],
    type: GenericSearchType.DateRange,
  },
];

export const groupSNSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.Status,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: SupervisionNoteStatus.Started, label: 'Started' },
      { id: SupervisionNoteStatus.Completed, label: 'Completed' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    disableDelete: true,
    defaultValue: [moment().subtract(14, 'days'), moment()],
    type: GenericSearchType.DateRange,
  },
];

export const sessionNoteSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.Status,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: [
      { id: 'STARTED', label: 'Started' },
      { id: 'AWAITING REVIEW', label: 'Awaiting Review' },
      { id: 'EDITS REQUESTED', label: 'Edits Requested' },
      { id: 'COMPLETED', label: 'Completed' },
      { id: 'AWAITING GUARDIAN SIGNATURE', label: 'Awaiting Guardian Signature' },
    ],
  },
  {
    id: HistorySearchParams.ClientId,
    label: 'Client Name',
    type: GenericSearchType.AutoComplete,
    options: [],
  },
  {
    id: 'dateRange',
    label: 'Date',
    disableDelete: true,
    defaultValue: [moment().subtract(7, 'days'), moment()],
    type: GenericSearchType.DateRange,
  },
];

export const invoiceSearchConfig: ISearchConfig[] = [
  {
    id: HistorySearchParams.ClientId,
    label: 'Client name',
    type: GenericSearchType.AutoComplete,
    placeholder: 'Select client',
    options: [],
  },
  {
    id: HistorySearchParams.Status,
    label: 'Status',
    type: GenericSearchType.AutoComplete,
    options: Object.values(InvoiceStatus).map((item) => ({
      id: item,
      label: item,
    })),
  },
];

export const getClaimSearchStr = (key: string, value: any) => {
  switch (key) {
    case 'startDate':
      return `${HistorySearchParams.StartDate}=${value.toISOString()}`;
    case 'endDate':
      return `${HistorySearchParams.EndDate}=${value.toISOString()}`;
    case 'invoice_history_attributes_only':
      return `invoice_history_attributes_only=${value}`;
    case HistorySearchParams.Status:
      return `${HistorySearchParams.Status}=${encodeURIComponent(value)}`;
    case HistorySearchParams.SecondaryStatus:
      return `${HistorySearchParams.SecondaryStatus}=${encodeURIComponent(value)}`;
    default:
      return `${key}=${encodeURIComponent(value)}`;
  }
};

export const trimsValuesInObject = (obj: any) => {
  const dir: any = {};
  for (const item in obj) {
    const trimmedValue = obj[item]?.trim?.();
    dir[item] = trimmedValue ?? obj[item];
  }
  return dir;
};

export const getThemeColor = (theme?: Themes) => {
  switch (theme) {
    case Themes.Primary:
      return FORTA_BLUE;

    default:
      return FORTA_BLUE;
  }
};

export const removeKey = (key: any, { [key]: _, ...rest }: any) => rest;

export const getCohortAssignmentSearchStr = (key: string, value: any) => {
  switch (key) {
    case 'client_id':
      return `client_id=${value}`;
    case 'cohort_id':
      return `cohort_id=${value}`;
    case 'date_start':
      return `date_start=${value}`;
    case 'date_end':
      return `date_end=${value}`;
    default:
      break;
  }
};

export const clientAssessmentReadOnly = (clientAssessment: IClientAssessment | null, user: any) => {
  return (
    clientAssessment?.status === AssessmentNoteStatus.Completed ||
    !areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor]) ||
    (clientAssessment && user?.email !== clientAssessment?.provider.email)
  );
};

export const getProviderAssignmentStatus = (assignment: IProviderAssignment) => {
  const { date_start, date_end, clinical_relationship } = assignment;

  if (!assignment.id || !clinical_relationship) return '-';
  if (date_end && moment(date_end).isBefore(moment(), 'day')) {
    return AssignmentStatus.UNASSIGNED;
  }
  if (moment(date_start).isAfter(moment(), 'day')) {
    return AssignmentStatus.PENDING;
  }
  return AssignmentStatus.ASSIGNED;
};

export const getInvoiceSearchStr = (key: string, value: any) => {
  if (Array.isArray(value)) {
    return value.map((val) => `${key}=${val}`).join('&');
  }

  switch (key) {
    case 'client_id':
      return `client_id=${value}`;
    case 'status':
      return `status=${value}`;
    default:
      break;
  }
};
